import { useQueryClient } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'
import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { logout } from '../../lib/api.ts'
import { disablePush, syncPushOnce } from '../../lib/push.ts'
import { ME_KEY, applyAccountPrefs, useSession } from '../../lib/queries.ts'

export function Splash() {
  return (
    <div className="splash" role="status" aria-label="Memuat">
      <span className="spinner" style={{ width: 28, height: 28, color: 'var(--teal)' }} />
    </div>
  )
}

function Blocked({ message, code }: { message: string; code: string }) {
  const qc = useQueryClient()
  const retry = () => qc.invalidateQueries({ queryKey: ME_KEY })
  const leave = async () => { await disablePush(); await logout().catch(() => {}); await retry() }
  return (
    <div className="page" style={{ paddingTop: 80 }}>
      <EmptyState
        Icon={ShieldAlert}
        title={code === 'license_expired' ? 'Lisensi sudah berakhir' : 'Belum bisa memverifikasi lisensi'}
        action={
          <div className="stack" style={{ width: '100%', maxWidth: 280 }}>
            {code !== 'license_expired' && <button className="btn btn-primary" onClick={retry}>Coba lagi</button>}
            <button className="btn btn-ghost" onClick={leave}>Keluar</button>
          </div>
        }
      >
        {message}
      </EmptyState>
    </div>
  )
}

/** Signed-in area. Anonymous visitors go to /masuk; a license that can't be used shows an explicit screen (no login loop). */
export function RequireSession() {
  const { data, isPending, isError, refetch } = useSession()
  const loc = useLocation()
  if (isPending) return <Splash />
  if (isError) {
    return (
      <div className="page" style={{ paddingTop: 80 }}>
        <EmptyState Icon={ShieldAlert} title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      </div>
    )
  }
  if (data.state === 'anon') return <Navigate to="/masuk" replace state={{ from: loc.pathname }} />
  if (data.state === 'blocked') return <Blocked message={data.message} code={data.code} />
  return <Outlet />
}

/** Everything except the consent screen requires the current Terms version to be accepted. */
export function RequireTerms() {
  const { data } = useSession()
  if (data?.state === 'ok' && data.me.needsTerms) return <Navigate to="/mulai/setuju" replace />
  return <Outlet />
}

/**
 * App pages need onboarding (/mulai) done once. The account's saved text size and Latin setting are applied on this
 * device, so they follow the user to a new phone.
 */
export function RequireOnboarding() {
  const { data } = useSession()
  const prefs = data?.state === 'ok' ? data.me.prefs : undefined
  useEffect(() => {
    if (prefs) applyAccountPrefs(prefs)
  }, [prefs?.fontScale, prefs?.latinOn]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (prefs?.reminder.enabled) syncPushOnce()
  }, [prefs?.reminder.enabled])
  if (prefs === null) return <Navigate to="/mulai" replace />
  return <Outlet />
}

/** /masuk: signed-in users skip it. */
export function PublicOnly() {
  const { data, isPending } = useSession()
  if (isPending) return <Splash />
  if (data?.state === 'ok') return <Navigate to="/" replace />
  return <Outlet />
}
